import { FC } from 'react';
import { Field, FileField, Image, withDatasourceCheck } from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';
import { sanitize } from 'isomorphic-dompurify';

import * as icons from '@gn-audio/icons';
import {
  ComponentProps,
  getContrastThemeClass,
  JabraIcon,
  resolveColorProperty,
} from '@gn-audio/jss-components';

import { JabraGallerySlider, JabraGallerySliderItem } from 'common/components/JabraGalerySlider';

type GenericKSPSpotLayout = 'vertical' | 'horizontal';

type GenericKSPCardItemFields = {
  text: Field<string>;
  image: FileField;
  backgroundColor: Field<string>;
  iconDropList: Field<string>;
  iconBackgroundColor: Field<string>;
  id: string;
  url?: string;
  name?: string;
};

type GenericKSPCardsSpotFields = {
  cards: GenericKSPCardItemFields[];
  layoutOrientation: Field<GenericKSPSpotLayout>;
  alternating: Field<boolean>;
};

type GenericKSPCardsSpotProps = {
  fields: GenericKSPCardsSpotFields;
} & ComponentProps;

type KSPSliderImageProps = {
  item: KSPSliderImage;
  index: number;
  active: boolean;
};

type KSPSliderImage = {
  identifier: string;
  image?: string;
  text?: string;
  displayName?: string;
  backgroundColor: string;
  layoutOrientation: GenericKSPSpotLayout;
  alternating: boolean;
  icon?: string;
  iconBackgroundColor: string;
};

const SliderImage = ({ item, index, active }: KSPSliderImageProps) => {
  return (
    <JabraGallerySliderItem
      active={active}
      index={index}
      className={classNames(
        'flex flex-shrink-0 basis-9/12 flex-col items-center overflow-hidden p-4',
        getClassNames(item),
        getContrastThemeClass(item.backgroundColor)
      )}
    >
      {item.layoutOrientation === 'vertical' && item.image ? (
        <VerticalImageLayout item={item} index={index} />
      ) : item.layoutOrientation === 'vertical' ? (
        <VerticalIconLayout item={item} />
      ) : (
        <HorizontalLayout item={item} />
      )}
    </JabraGallerySliderItem>
  );
};

const getClassNames = (item: KSPSliderImage) => {
  if (item.layoutOrientation === 'horizontal') {
    return 'md:basis-1/2 lg:basis-1/2 2xl:basis-1/3';
  } else if (item.icon && !item.image) {
    return 'md:basis-1/2 lg:basis-1/3 2xl:basis-1/4';
  } else {
    return 'md:basis-1/3 lg:basis-1/3 2xl:basis-1/4';
  }
};

const VerticalImageLayout = ({ item, index }: { item: KSPSliderImage; index: number }) => {
  return (
    <div
      className={classNames(
        'flex h-full w-full min-w-[100px] flex-col rounded-3xl',
        getContrastThemeClass(item.backgroundColor)
      )}
      style={{ backgroundColor: item.backgroundColor }}
    >
      {item.alternating && index % 2 !== 0 ? (
        <>
          {item.text && (
            <div className="flex-grow p-6 text-center text-base font-normal leading-6">
              <span dangerouslySetInnerHTML={{ __html: sanitize(item.text) }} />
            </div>
          )}
          <Image
            className="h-[200px] w-full rounded-b-3xl object-cover"
            field={{
              src: item.image,
              alt: item.displayName,
            }}
            loading="lazy"
          />
        </>
      ) : (
        <>
          <Image
            className="h-[200px] w-full rounded-t-3xl object-cover"
            field={{
              src: item.image,
              alt: item.displayName,
            }}
            loading="lazy"
          />
          {item.text && (
            <div className="flex flex-grow p-6 text-center text-base font-normal leading-6">
              <span dangerouslySetInnerHTML={{ __html: sanitize(item.text) }} />
            </div>
          )}
        </>
      )}
    </div>
  );
};

const VerticalIconLayout = ({ item }: { item: KSPSliderImage }) => (
  <div className="flex h-full w-full min-w-[100px] flex-col items-center justify-start">
    <div
      className="flex items-center justify-center rounded-full p-2"
      style={{ backgroundColor: item.backgroundColor }}
    >
      <div
        className="flex items-center justify-center rounded-full p-6"
        style={{ backgroundColor: item.iconBackgroundColor }}
      >
        {item.icon && (
          <JabraIcon
            className={classNames('size-10', getContrastThemeClass(item.iconBackgroundColor))}
            Icon={icons[item.icon as keyof typeof icons]}
          />
        )}
      </div>
    </div>

    <div
      className="-z-10 -mt-[42px] size-full rounded-xl p-6 pt-12 text-center text-base font-normal leading-6"
      style={{ backgroundColor: item.backgroundColor }}
    >
      {item.text && <span dangerouslySetInnerHTML={{ __html: sanitize(item.text) }}></span>}
    </div>
  </div>
);

const HorizontalLayout = ({ item }: { item: KSPSliderImage }) => {
  return (
    <div
      className={classNames(
        'flex min-h-28 w-full flex-grow flex-col items-center justify-center gap-2 overflow-hidden rounded-xl sm:flex-row sm:rounded-full sm:text-left',
        getContrastThemeClass(item.backgroundColor)
      )}
      style={{ backgroundColor: item.backgroundColor }}
    >
      {item.image ? (
        <Image
          className="flex shrink-0 object-cover sm:size-28 sm:rounded-full"
          field={{
            src: item.image,
            alt: item.displayName,
          }}
          loading="lazy"
        />
      ) : item.icon ? (
        <div
          className="mb-6 ml-0 mt-6 flex size-20 items-center justify-center rounded-full sm:ml-6 md:mb-0 md:mt-0"
          style={{ backgroundColor: item.iconBackgroundColor || 'rgb(0 0 0 / 0.1)' }}
        >
          <JabraIcon
            className={classNames(
              'size-10 sm:mx-6',
              getContrastThemeClass(item.iconBackgroundColor)
            )}
            Icon={icons[item.icon as keyof typeof icons]}
          />
        </div>
      ) : null}

      <div className="flex w-full flex-grow justify-center pb-14 pl-6 pr-8 text-left font-normal leading-6 sm:pb-4 sm:pl-2 sm:pr-14 sm:pt-4 md:text-sm">
        {item.text && <span dangerouslySetInnerHTML={{ __html: sanitize(item.text) }}></span>}
      </div>
    </div>
  );
};

const GenericKSPCardsSpot: FC = ({ fields }: GenericKSPCardsSpotProps) => {
  if (!fields?.cards?.length) {
    return null;
  }

  const sliderCards = prepareSliderCards(fields);

  if (!sliderCards?.length) {
    return null;
  }

  return (
    <div className="max-md:container-fluid md:container">
      <JabraGallerySlider
        itemsAmountStep={1}
        items={sliderCards}
        itemComponent={SliderImage}
        showButtons={true}
        oneItem="max-sm"
        loop={false}
      />
    </div>
  );
};

const DEFAULT_BACKGROUND_COLOR = '#ffd100';
const DEFAULT_ICON_BACKGROUND_COLOR = '#A88C70';

const prepareSliderCards = (fields: GenericKSPCardsSpotFields): KSPSliderImage[] => {
  if (!fields) return [];

  const layoutOrientation = fields.layoutOrientation?.value || 'vertical';
  const alternating = fields?.alternating?.value || false;

  return fields.cards
    .map((card) => {
      const icon = card.iconDropList?.value;
      const iconBackgroundColor = card.iconBackgroundColor?.value;
      const img = card.image?.value;
      const text = card.text?.value;
      const backgroundColor = card.backgroundColor?.value;

      if (!img?.src && !text && !icon) return null;

      return {
        identifier: card.id,
        image: img?.src,
        text,
        displayName: img?.displayName,
        backgroundColor: resolveColorProperty(backgroundColor || DEFAULT_BACKGROUND_COLOR),
        layoutOrientation,
        alternating,
        icon,
        iconBackgroundColor: resolveColorProperty(
          iconBackgroundColor || DEFAULT_ICON_BACKGROUND_COLOR
        ),
      };
    })
    .filter(Boolean) as KSPSliderImage[];
};

export default withDatasourceCheck()(GenericKSPCardsSpot);
